import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import {
  EventList,
  IpList,
  KpiCard,
  LoadingBoard,
  MonitorForm,
  MonitoredList,
  ProjectCard,
  SecurityHeader,
  SecurityTabs,
} from '../components/security/SecurityLists';
import { projectOptions, securityEventPath } from '../components/security/securityLabels';
import {
  apiCreateCybersecurityMonitored,
  apiGetCybersecurityEvents,
  apiGetCybersecurityIpCounters,
  apiGetCybersecurityMonitored,
  apiGetCybersecuritySummary,
  apiGetRegistryVehicleUpdates,
  apiRevertCybersecurityMonitored,
} from '../api/client';
import { formatWhen } from '../components/security/SecurityDetail';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'ips', label: 'Addresses' },
  { id: 'events', label: 'Events' },
  { id: 'updates', label: 'Vehicle updates' },
  { id: 'monitored', label: 'Watch list' },
];

export default function CybersecurityPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({ sources: [] });
  const [ips, setIps] = useState([]);
  const [events, setEvents] = useState([]);
  const [monitored, setMonitored] = useState([]);
  const [registryUpdates, setRegistryUpdates] = useState({ count: 0, threshold: 500, rows: [] });
  const [actorKey, setActorKey] = useState('');
  const [monitorSource, setMonitorSource] = useState('main');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [sum, counters, ev, mon, updates] = await Promise.all([
        apiGetCybersecuritySummary(),
        apiGetCybersecurityIpCounters(),
        apiGetCybersecurityEvents(),
        apiGetCybersecurityMonitored(),
        apiGetRegistryVehicleUpdates('today').catch(() => ({ count: 0, threshold: 500, rows: [] })),
      ]);
      setSummary(sum || { sources: [] });
      setIps(counters?.rows || []);
      setEvents(ev?.rows || []);
      setMonitored(mon?.rows || []);
      setRegistryUpdates(updates || { count: 0, threshold: 500, rows: [] });
    } catch (e) {
      setError(e.message || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const options = projectOptions(summary.sources);
    if (options.length && !options.some((item) => item.value === monitorSource)) {
      setMonitorSource(options[0].value);
    }
  }, [summary.sources, monitorSource]);

  const onRevert = async (source, id) => {
    try {
      await apiRevertCybersecurityMonitored(source, id);
      await load();
    } catch (e) {
      setError(e.message || 'Could not return this person to normal');
    }
  };

  const onMonitor = async (event) => {
    event.preventDefault();
    if (!actorKey.trim()) return;
    setSaving(true);
    try {
      await apiCreateCybersecurityMonitored(monitorSource, {
        actor_key: actorKey.trim(),
        reason: 'admin',
      });
      setActorKey('');
      await load();
    } catch (e) {
      setError(e.message || 'Could not add this person to the watch list');
    } finally {
      setSaving(false);
    }
  };

  const criticalOpen = (summary.sources || []).reduce(
    (sum, source) => sum + (source.critical_unnotified || 0),
    0,
  );
  const monitoredActive = (summary.sources || []).reduce(
    (sum, source) => sum + (source.monitored_active || 0),
    0,
  );
  const ipTotal = (summary.sources || []).reduce(
    (sum, source) => sum + (source.ip_counter_total || 0),
    0,
  );
  const alertAt = summary.sources?.[0]?.ip_block_alert || 200;
  const mainSource = (summary.sources || []).find((source) => source.source === 'main') || {};
  const registryCount = registryUpdates.count ?? mainSource.registry_updates_today ?? 0;
  const registryAlert = registryUpdates.threshold || mainSource.registry_update_alert || 500;
  const registryOver = registryCount >= registryAlert;
  const needsAttention = criticalOpen > 0 || registryOver;

  return (
    <AppLayout>
      <div className="page-content sec-page">
        <SecurityHeader
          title="Cybersecurity"
          subtitle="See what needs attention, then open a project for the full picture."
          alert={needsAttention}
          statusText={needsAttention
            ? `${criticalOpen} urgent item${criticalOpen === 1 ? '' : 's'} across connected projects`
            : 'All connected projects look calm'}
          onRefresh={load}
        />

        {error && <div className="sec-alert">{error}</div>}

        <SecurityTabs tabs={TABS} active={tab} onChange={setTab} />

        {loading && <LoadingBoard />}

        {!loading && tab === 'overview' && (
          <div className="animate-fadeIn">
            <div className="sec-kpi-grid">
              <KpiCard
                label="Urgent"
                value={criticalOpen}
                danger={!!criticalOpen}
                hint="View events →"
                onClick={() => setTab('events')}
              />
              <KpiCard
                label="Addresses seen"
                value={ipTotal}
                hint="Investigate addresses →"
                onClick={() => setTab('ips')}
              />
              <KpiCard
                label="On the watch list"
                value={monitoredActive}
                hint="Open watch list →"
                onClick={() => setTab('monitored')}
              />
              <KpiCard label="Block threshold" value={alertAt} />
              <KpiCard
                label="Non-officer updates today"
                value={registryCount}
                danger={registryOver}
                hint={registryOver ? `Reached ${registryAlert} — alert sent` : `Threshold ${registryAlert} · View list →`}
                onClick={() => setTab('updates')}
              />
            </div>
            {registryOver && (
              <div className="sec-alert">
                Tracking Cars reached {registryCount} non-officer vehicle updates today (threshold {registryAlert}).
              </div>
            )}

            <h2 className="sec-section-title">Projects</h2>
            <div className="sec-project-grid">
              {(summary.sources || []).map((source) => (
                <ProjectCard
                  key={source.source}
                  source={source}
                  onOpen={(id) => navigate(`/cybersecurity/project/${id}`)}
                />
              ))}
              {(summary.sources || []).length === 0 && (
                <div className="sec-empty">
                  <strong>No projects yet</strong>
                  Connect a project to start monitoring.
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && tab === 'ips' && (
          <IpList
            ips={ips}
            group={group}
            alertAt={alertAt}
            onFilter={setGroup}
            onOpen={(row) => navigate(`/cybersecurity/ip/${row.backend}/${encodeURIComponent(row.ip)}`)}
          />
        )}

        {!loading && tab === 'updates' && (
          <div className="sec-list animate-fadeIn">
            <div className="sec-row" style={{ cursor: 'default' }}>
              <p className="sec-row__meta" style={{ margin: 0 }}>
                Third-party registry updates today · {registryCount} of {registryAlert}
              </p>
            </div>
            {(registryUpdates.rows || []).length === 0 && (
              <div className="sec-empty">
                <strong>No updates today</strong>
                No third-party vehicle updates recorded.
              </div>
            )}
            {(registryUpdates.rows || []).map((row) => (
              <button
                key={row.id}
                type="button"
                className="sec-row"
                onClick={() => navigate(`/cybersecurity/registry-update/${row.id}`)}
              >
                <div className="sec-row__top">
                  <strong>{row.vin}</strong>
                  <span className="sec-row__when">{formatWhen(row.applied_at)}</span>
                </div>
                <p className="sec-row__meta">
                  {[row.year, row.make, row.model].filter(Boolean).join(' ') || 'Vehicle'}
                  {row.tracking_number ? ` · ${row.tracking_number}` : ''}
                  {row.source_system ? ` · ${row.source_system}` : ''}
                </p>
              </button>
            ))}
          </div>
        )}

        {!loading && tab === 'events' && (
          <EventList
            events={events}
            group={group}
            onFilter={setGroup}
            onOpen={(row) => {
              const path = securityEventPath(row);
              if (path) navigate(path);
            }}
          />
        )}

        {!loading && tab === 'monitored' && (
          <div>
            <MonitorForm
              actorKey={actorKey}
              setActorKey={setActorKey}
              monitorSource={monitorSource}
              setMonitorSource={setMonitorSource}
              sources={summary.sources}
              saving={saving}
              onSubmit={onMonitor}
            />
            <MonitoredList
              rows={monitored}
              onOpen={(row) => navigate(`/cybersecurity/monitored/${row.backend}/${row.id}`)}
              onRevert={onRevert}
            />
          </div>
        )}
      </div>
    </AppLayout>
  );
}
